// Ostrzezenia w loggerze: plan pasm IARU Region 1 (segmenty emisji) oraz
// wlasne uprawnienia usera (dozwolone pasma, maks. moc -- ustawiane w profilu).
// To PODPOWIEDZI, nie blokada zapisu -- plan pasm to zalecenie IARU, a nie
// przepis, i zdarzaja sie uzasadnione wyjatki (np. zawody, DX-pedycje).
import { freqToBandJs } from './utils';
import { tx } from './i18n';

// Przydzialy Regionu 1 (kHz). Czestotliwosc w pasmie wg freqToBandJs, ale poza
// ta tabela = typowo przydzial innego regionu (np. 7.250 MHz w USA).
const R1_BANDS = {
  '160m': [1810, 2000], '80m': [3500, 3800], '60m': [5351.5, 5366.5], '40m': [7000, 7200],
  '30m': [10100, 10150], '20m': [14000, 14350], '17m': [18068, 18168], '15m': [21000, 21450],
  '12m': [24890, 24990], '10m': [28000, 29700], '6m': [50000, 52000], '2m': [144000, 146000],
  '70cm': [430000, 440000], '23cm': [1240000, 1300000],
};

// Segmenty: [od kHz, do kHz, rodzaj]
//   cw     -- tylko CW
//   narrow -- CW + waskopasmowe emisje cyfrowe (FT8, RTTY, PSK...)
//   nofm   -- wszystko poza FM (waskie emisje na UKF: CW/SSB/cyfrowe)
//   beacon -- radiolatarnie (nie nadawac)
//   all    -- wszystkie emisje
// Uproszczone do tego, co realnie pomaga uniknac pomylki (np. SSB w czesci CW).
const SEGMENTS = [
  [1810, 1838, 'cw'], [1838, 1843, 'narrow'],
  [3500, 3570, 'cw'], [3570, 3600, 'narrow'],
  [5351.5, 5354, 'narrow'],
  [7000, 7040, 'cw'], [7040, 7053, 'narrow'],
  [10100, 10130, 'cw'], [10130, 10150, 'narrow'],
  [14000, 14070, 'cw'], [14070, 14099, 'narrow'], [14099, 14101, 'beacon'], [14101, 14112, 'narrow'],
  [18068, 18095, 'cw'], [18095, 18109, 'narrow'], [18109, 18111, 'beacon'],
  [21000, 21070, 'cw'], [21070, 21149, 'narrow'], [21149, 21151, 'beacon'],
  [24890, 24915, 'cw'], [24915, 24929, 'narrow'], [24929, 24931, 'beacon'],
  [28000, 28070, 'cw'], [28070, 28190, 'narrow'], [28190, 28225, 'beacon'],
  [50000, 50100, 'cw'], [50100, 50500, 'nofm'],
  [144000, 144110, 'cw'], [144110, 144400, 'nofm'], [144400, 144490, 'beacon'],
  [432000, 432100, 'cw'], [432100, 432400, 'nofm'], [432400, 432490, 'beacon'],
];

const NARROW_DIGI = new Set(['FT8', 'FT4', 'RTTY', 'PSK', 'PSK31', 'PSK63', 'PSK125', 'JT65', 'JT9',
  'WSPR', 'OLIVIA', 'JS8', 'MFSK', 'CONTESTIA', 'HELL', 'Q65', 'FST4', 'MSK144', 'DIGI', 'DATA']);

export function modeClass(mode) {
  const m = (mode || '').toUpperCase();
  if (m === 'CW') return 'cw';
  if (NARROW_DIGI.has(m)) return 'narrow';
  if (m === 'FM' || m === 'C4FM' || m === 'DMR' || m === 'DSTAR' || m === 'DV') return 'fm';
  return 'phone'; // SSB/USB/LSB/AM/SSTV/FreeDV i reszta
}

const SEG_LABEL = {
  cw: tx('segment tylko CW'), narrow: tx('segment CW i wąskich emisji cyfrowych'),
  nofm: tx('segment emisji wąskopasmowych (bez FM)'), beacon: tx('segment radiolatarni'),
};

function segmentFor(khz) {
  for (const [lo, hi, kind] of SEGMENTS) {
    if (khz >= lo && khz < hi) return kind;
  }
  return 'all';
}

// Zwraca liste ostrzezen [{level: 'warn'|'info', text}] (pusta = wszystko OK).
export function bandPlanWarnings(freqMhz, mode, power, userProfile) {
  const out = [];
  const f = typeof freqMhz === 'number' ? freqMhz : parseFloat(String(freqMhz || '').replace(',', '.'));
  if (!f || isNaN(f)) return out;
  const band = freqToBandJs(f);
  const khz = f * 1000;

  if (band && R1_BANDS[band]) {
    const [lo, hi] = R1_BANDS[band];
    if (khz < lo || khz > hi) {
      out.push({ level: 'warn', text: tx('{0} MHz jest poza przydziałem pasma {1} w Regionie 1 ({2}–{3} MHz).', [f.toFixed(3), band, lo / 1000, hi / 1000]) });
    } else if (mode) {
      const seg = segmentFor(khz);
      const mc = modeClass(mode);
      const ok = seg === 'all'
        || (seg === 'cw' && mc === 'cw')
        || (seg === 'narrow' && (mc === 'cw' || mc === 'narrow'))
        || (seg === 'nofm' && mc !== 'fm');
      if (seg === 'beacon') {
        out.push({ level: 'warn', text: tx('{0} MHz to {1} (IARU R1) — nie nadawaj tutaj.', [f.toFixed(3), SEG_LABEL.beacon]) });
      } else if (!ok) {
        out.push({ level: 'warn', text: tx('{0} na {1} MHz: to {2} wg planu IARU Region 1.', [mode.toUpperCase(), f.toFixed(3), SEG_LABEL[seg]]) });
      }
      if (mc === 'fm' && f < 29 && f >= 1.8) {
        out.push({ level: 'warn', text: tx('FM na KF zalecane tylko w 29.0–29.7 MHz (plan IARU R1).') });
      }
    }
  }

  // Wlasne uprawnienia (z profilu)
  const allowed = userProfile?.license_bands || [];
  if (band && band !== 'other' && allowed.length && !allowed.includes(band)) {
    out.push({ level: 'warn', text: tx('Pasmo {0} nie jest na liście Twoich uprawnień (Ustawienia → Profil).', [band]) });
  }
  const maxP = userProfile?.license_max_power;
  const p = parseFloat(power);
  if (maxP && p && p > maxP) {
    out.push({ level: 'warn', text: tx('Moc {0} W przekracza Twój limit {1} W.', [p, maxP]) });
  }
  return out;
}

export const LICENSE_BAND_OPTIONS = Object.keys(R1_BANDS);
